import { OnaTips } from "@/components/ai/chat/OnaTips";
import { Banner } from "@/components/flexkit/Banner";
import type { AgentMessageFragment, Message } from "@/hooks/use-stream-conversation";
import {
    isAnsweredInteractiveFragment,
    type PendingInteractiveFragment,
} from "@/components/ai/pending-interactive-utils";
import { Agents, type PlainAgentExecution } from "@/queries/agent-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { getActiveEnvironmentId } from "@/utils/agent-executions";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { AgentExecution_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";
import { forwardRef, useEffect, useMemo, useState } from "react";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { GroupedMessages } from "@/components/ai/chat/GroupedMessages";

// OnaTips fade-out duration when conversation starts
// Kept short (300ms) to minimize gap before first message appears
const ONA_TIPS_FADE_OUT_MS = 300;

export type ChatMessagesHandle = {
    scrollToBottom: () => void;
};

type ChatMessagesProps = {
    messages: Message[];
    onClick?: (block: AgentMessageFragment) => void;
    agentExecution: PlainAgentExecution;
    enableTips?: boolean;
    onSendMessage?: (message: string) => void;
    /** Fragment to hide from inline rendering (shown above input instead) */
    pendingInteractiveBlock?: PendingInteractiveFragment | null;
    /** When true, hides interactive elements like voting buttons and next-step proposals */
    readOnly?: boolean;
} & PropsWithClassName;

export const ChatMessages = forwardRef<ChatMessagesHandle, ChatMessagesProps>(
    (
        {
            messages,
            onClick,
            agentExecution,
            className,
            enableTips = true,
            onSendMessage,
            pendingInteractiveBlock,
            readOnly,
        },
        ref,
    ) => {
        const environmentId = getActiveEnvironmentId(agentExecution);

        // Filter out interactive blocks from inline rendering:
        // - Pending interactive blocks (shown above input instead)
        // - Answered interactive blocks (should disappear after user responds)
        // - Next-step proposals when in readOnly mode
        const filteredMessages = useMemo(() => {
            return messages.map((message, messageIndex) => {
                if (message.type !== "agent") return message;

                const filteredFragments = message.fragments.filter((fragment) => {
                    // Filter out pending interactive block (shown above input)
                    if (pendingInteractiveBlock && fragment.id === pendingInteractiveBlock.id) {
                        return false;
                    }
                    // Filter out answered interactive blocks (should disappear after click)
                    if (isAnsweredInteractiveFragment(fragment, messageIndex, messages)) {
                        return false;
                    }
                    // Filter out next-step proposals in readOnly mode
                    if (readOnly && fragment.type === "nextStepsProposal") {
                        return false;
                    }
                    return true;
                });

                // If no fragments changed, return original message
                if (filteredFragments.length === message.fragments.length) return message;

                return { ...message, fragments: filteredFragments };
            });
        }, [messages, pendingInteractiveBlock, readOnly]);
        const { data: environment } = useEnvironment(environmentId);

        // Check if we should show Ona tips
        // Show tips while environment is starting OR until agent starts responding (whichever is longer)
        const isEnvironmentStarting =
            environment?.status?.phase &&
            (environment.status.phase === EnvironmentPhase.STARTING ||
                environment.status.phase === EnvironmentPhase.CREATING ||
                environment.status.phase === EnvironmentPhase.UPDATING);

        const hasAgentResponded = messages.length >= 2; // User message + at least one agent message

        const shouldShowOnaTips =
            enableTips &&
            !hasAgentResponded && // Agent hasn't started responding yet
            agentExecution?.spec?.agentId === Agents.InEnvironmentOna.id && // In-environment agent
            agentExecution?.status?.phase !== AgentExecution_Phase.STOPPED && // Conversation is not stopped
            environment?.status?.phase && // Environment data exists
            isEnvironmentStarting;

        // State for managing fade-out animation
        const [isVisible, setIsVisible] = useState(shouldShowOnaTips);
        const [shouldRender, setShouldRender] = useState(shouldShowOnaTips);

        useEffect(() => {
            if (shouldShowOnaTips) {
                setShouldRender(true);
                setIsVisible(true);
            } else if (shouldRender) {
                // Start fade out
                setIsVisible(false);
                // Remove from DOM after animation completes
                const timer = setTimeout(() => {
                    setShouldRender(false);
                }, ONA_TIPS_FADE_OUT_MS);
                return () => clearTimeout(timer);
            }
        }, [shouldShowOnaTips, shouldRender]);

        return (
            <div
                className={cn(
                    "mx-auto flex h-full w-full max-w-[700px] flex-col items-start gap-4 p-4 sm:p-6",
                    className,
                )}
            >
                <GroupedMessages
                    ref={ref}
                    messages={filteredMessages}
                    agentExecution={agentExecution}
                    onClick={onClick}
                    onSendMessage={onSendMessage}
                    readOnly={readOnly}
                    data-tracking-id="default-tracking-id"
                />

                {shouldRender && (
                    <div
                        className={cn(
                            "w-full transition-opacity duration-300 ease-out",
                            isVisible ? "opacity-100" : "opacity-0",
                        )}
                    >
                        <OnaTips environment={environment} />
                    </div>
                )}
                {agentExecution?.status?.warningMessage && (
                    <Banner
                        variant="warning"
                        text={agentExecution.status.warningMessage}
                        data-testid="agent-execution-warning-banner"
                    />
                )}
                {agentExecution?.status?.failureMessage && (
                    <Banner
                        variant="danger"
                        text={agentExecution.status.failureMessage}
                        data-testid="agent-execution-failure-banner"
                    />
                )}
            </div>
        );
    },
);

ChatMessages.displayName = "ChatMessages";
